import React from 'react';
import { Container, Sprite, withPixiApp, Graphics, Text } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { BoxIoService } from '../BoxIoService';

const button_image =
  'https://teo-s3-prod.s3.eu-west-1.amazonaws.com/PIXI/rotarybutton.png';

const SNAP_ANGLES = [
  0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170,
  180, 190, 200, 210, 220, 230, 240, 250, 260, 270, 280, 290,
];

const styleUnit = new PIXI.TextStyle({
  fontFamily: 'Arial',
  fontSize: 80,
  fontStyle: 'italic',
  strokeThickness: 1,
  wordWrap: true,
  wordWrapWidth: 440,
  fontWeight: 400,
});

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/**
 * RotaryButton
 *
 * Supports 2 modes:
 * - mode="analog" (default): maps knob to PWM output (0..24, or vmin..vmax)
 * - mode="servo": maps knob to servo angle (vmin..vmax, default 0..180)
 *
 * Display:
 * - percent (default): show % based on knob position
 * - value: show the mapped output value + unit
 */
const RotaryButton = withPixiApp(
  class extends React.Component {
    displayName = 'RotaryButton';

    static defaultProps = {
      mode: 'analog', // 'analog' | 'servo'
      display: 'percent', // 'percent' | 'value'
      unit: '%',
    };

    constructor(props) {
      super();
      this.state = {
        angle: 0,
        value: 0, // mapped output (e.g. volts/24-scale or degrees)
        disabled: false,
      };

      this.service = BoxIoService.getInstance();
      this.subscriptions = [];

      // unified state update from incoming values
      const applyIncomingValue = (rawValue) => {
        // rawValue is whatever BoxIoService puts in state[name].value
        // For PWM you'll store 0..24 (or vmin..vmax), for servo you'll store degrees.

        let knob24 = rawValue;

        // If vmin/vmax are provided, treat rawValue as in that range and map to 0..24 for display
        if (this.props.vmin != null || this.props.vmax != null) {
          const vmin = this.props.vmin ?? 0;
          const vmax = this.props.vmax ?? 24;
          if (vmax !== vmin) {
            knob24 = ((rawValue - vmin) / (vmax - vmin)) * 24;
          } else {
            knob24 = 0;
          }
        }

        const angle = (knob24 / 24) * 299;
        this.setState({ value: rawValue, angle });
      };

      // Subscribe based on mode.
      // For now, both PWM + servo updates come through onAnalog in your architecture
      // (servo writes call setAnalog in BoxIoService).
      this.subscriptions.push(
        this.service.onAnalog.subscribe((msg) => {
          if (msg.name !== props.name) return;
          if (this.service.isStateLocked(msg.name)) return;
          if (Date.now() - (this.timestamp || 0) < 100) return;

          applyIncomingValue(msg.value);
        })
      );

      this.subscriptions.push(
        this.service.onStatelock.subscribe(({ name, locked }) => {
          if (name !== props.name) return;
          const v = this.service.state?.[name]?.value ?? 0;
          // keep knob aligned with current value
          let knob24 = v;
          if (this.props.vmin != null || this.props.vmax != null) {
            const vmin = this.props.vmin ?? 0;
            const vmax = this.props.vmax ?? 24;
            knob24 = vmax !== vmin ? ((v - vmin) / (vmax - vmin)) * 24 : 0;
          }
          this.setState({
            value: v,
            angle: (knob24 / 24) * 299,
            disabled: !!locked,
          });
        })
      );
    }

    componentWillUnmount() {
      for (const subscription of this.subscriptions) subscription.unsubscribe();
      this.subscriptions = [];
    }

    componentDidMount() {
      const current = this.props.value ?? 0;

      let knob24 = current;
      if (this.props.vmin != null || this.props.vmax != null) {
        const vmin = this.props.vmin ?? 0;
        const vmax = this.props.vmax ?? 24;
        knob24 = vmax !== vmin ? ((current - vmin) / (vmax - vmin)) * 24 : 0;
      }

      this.setState({
        angle: (knob24 / 24) * 299,
        value: current,
        disabled: this.service.isStateLocked(this.props.name),
      });
    }

    onOrientStart = (event) => {
      const sprite = event.currentTarget;
      sprite.orienting = this.state.disabled ? false : true;
      event.data.originalEvent.target.cancelScroll = true;
    };

    onOrientEnd = (event) => {
      const sprite = event.currentTarget;
      if (sprite.orienting) sprite.orienting = false;
    };

    writeValue = (mappedValue) => {
      // allow consumer override
      if (typeof this.props.write === 'function') {
        this.props.write(this.props.name, mappedValue);
        return;
      }

      // default routing based on mode
      if (this.props.mode === 'servo') {
        this.service.sendServo(this.props.name, mappedValue);
      } else {
        this.service.sendAnalog(this.props.name, mappedValue);
      }
    };

    onOrientMove = (event) => {
      const sprite = event.currentTarget;
      if (!sprite.orienting) return;

      event.data.originalEvent.target.cancelScroll = true;

      const spritePos = sprite.getGlobalPosition();
      const x = event.data.global.x - spritePos.x;
      const y = event.data.global.y - spritePos.y;

      let rad = Math.atan2(y, x);
      let deg = rad * (180 / Math.PI);

      deg -= 130;
      if (deg < 0) deg += 360;

      if (deg > 320) deg = 0;
      else if (deg > 299) deg = 299;

      this.setState({ angle: deg });

      // Knob position on 0..24 scale
      const knob24 = (deg / 299) * 24;

      // Map to output value
      const hasRange = this.props.vmin != null || this.props.vmax != null;
      const vmin = this.props.vmin ?? (this.props.mode === 'servo' ? 0 : 0);
      const vmax = this.props.vmax ?? (this.props.mode === 'servo' ? 180 : 24);

      let out = knob24;

      if (hasRange) {
        out = vmin + (knob24 / 24) * (vmax - vmin);
      } else {
        // no range: keep legacy analog behavior (0..24)
        // servo mode without vmin/vmax defaults to 0..180
        if (this.props.mode === 'servo') {
          out = (knob24 / 24) * 180;
        }
      }

      // Clean output a bit
      if (this.props.mode === 'servo') out = Math.round(out);
      // (analog can stay float)

      this.timestamp = Date.now();
      this.setState({ value: out });

      this.writeValue(out);
    };

    renderValueText() {
      const { display, unit } = this.props;
      const { angle, value } = this.state;

      if (display === 'value') {
        // show actual output value
        const txt =
          this.props.mode === 'servo'
            ? `${Math.round(value)}${unit || '°'}`
            : `${Number(value).toFixed(2)}${unit ? ` ${unit}` : ''}`;
        return txt;
      }

      // default: percent based on knob position
      return ((angle / 299) * 100).toFixed(0) + '%';
    }

    render() {
      const { scaleRot } = this.props;
      const disabled = this.state.disabled;

      return (
        <Container
          {...this.props}
          pivot={{ y: -350, x: 205 }}
          scale={scaleRot}
          width={87}
          height={79}
        >
          <Sprite
            alpha={disabled ? 0.6 : 1}
            angle={this.state.angle}
            image={button_image}
            anchor={0.5}
            x={0}
            y={0}
            interactive
            buttonMode
            pointerdown={this.onOrientStart}
            pointerup={this.onOrientEnd}
            pointerupoutside={this.onOrientEnd}
            pointermove={this.onOrientMove}
          />

          {SNAP_ANGLES.map((x, index) => (
            <Graphics
              key={index}
              alpha={disabled ? 0.6 : 1}
              anchor={0.5}
              angle={x + 35}
              draw={(g) => {
                g.clear();
                g.lineStyle(10, this.state.angle <= x ? 0x000000 : 0xfccc4a);
                g.drawRect(0, 200, 0, 40);
                g.endFill();
              }}
            />
          ))}

          <Text
            anchor={0.5}
            scale={0.5}
            x={0}
            y={200}
            text={this.renderValueText()}
            style={styleUnit}
          />
        </Container>
      );
    }
  }
);

export default RotaryButton;
